import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  downloadZipUrl,
  getFile,
  listFiles,
  runTestsForCrawl,
  waitForRunFinish,
  type FileInfo,
} from "../lib/api";
import { SendAsPrDialog } from "../components/SendAsPrDialog";
import { CrawlTabs } from "../components/CrawlTabs";

/**
 * Test Generator — file browser for a single crawl's emitted project.
 * No syntax highlighter lib — a plain <pre> with line numbers keeps the v0
 * bundle small and the visual language identical to the mockup's code panel.
 */
export function TestGeneratorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [files, setFiles] = useState<FileInfo[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [contents, setContents] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sendAsPrOpen, setSendAsPrOpen] = useState(false);
  // Run-with-trace state. `runStatus` walks idle → starting → running → done.
  // The status caption swaps below the button for visible progress without
  // needing a modal.
  const [runStatus, setRunStatus] = useState<
    "idle" | "starting" | "running" | "done" | "failed"
  >("idle");
  const [runError, setRunError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    listFiles(id)
      .then((f) => {
        if (cancelled) return;
        setFiles(f);
        // Default to the most interesting file if one exists — prefer a test class.
        const preferred = pickDefaultFile(f);
        if (preferred) setSelected(preferred.name);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id || !selected) return;
    let cancelled = false;
    setContents(null);
    getFile(id, selected)
      .then((c) => {
        if (!cancelled) setContents(c);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id, selected]);

  if (!id) return <div>Missing crawl id.</div>;
  if (error) return <ErrorPane message={error} />;

  return (
    <div>
      <header className="mb-6">
        <div className="flex items-center gap-3 text-xs text-slate-500 font-mono">
          <Link to="/" className="hover:text-slate-300">
            dashboard
          </Link>
          <span>/</span>
          <Link to={`/crawls/${encodeURIComponent(id)}`} className="hover:text-slate-300">
            {id}
          </Link>
          <span>/</span>
          <span className="text-slate-300">tests</span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Generated tests</h1>
            <p className="text-sm text-slate-400 mt-1">
              Files emitted by the Selenium code generator. Ready to paste into Maven or pytest.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <a
              href={files && files.length > 0 ? downloadZipUrl(id) : undefined}
              aria-disabled={!files || files.length === 0}
              // <a> download hint — browsers will honour the server's
              // Content-Disposition filename, but this is a fallback name.
              download={`${id}.zip`}
              onClick={(e) => {
                if (!files || files.length === 0) e.preventDefault();
              }}
              className={`rounded-md px-3 py-2 text-sm font-medium border border-slate-700 text-slate-200 hover:bg-slate-800 ${
                !files || files.length === 0
                  ? "opacity-50 pointer-events-none"
                  : ""
              }`}
            >
              Download ZIP
            </a>
            <button
              type="button"
              disabled={
                !files ||
                files.length === 0 ||
                runStatus === "starting" ||
                runStatus === "running"
              }
              onClick={async () => {
                if (!id) return;
                setRunStatus("starting");
                setRunError(null);
                try {
                  const { run } = await runTestsForCrawl(id);
                  setRunStatus("running");
                  // Poll until terminal, then route the user straight to the
                  // trace viewer — that's where the per-test detail and the
                  // Triage tab (§16.6) live.
                  await waitForRunFinish(run.id);
                  setRunStatus("done");
                  navigate(`/runs/${encodeURIComponent(run.id)}/trace`);
                } catch (err) {
                  setRunError(err instanceof Error ? err.message : String(err));
                  setRunStatus("failed");
                }
              }}
              className="rounded-md bg-emerald-500 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50"
              title="Server-side runs `uaip run --trace`. When it finishes, you'll land on the trace viewer."
            >
              {runStatus === "starting"
                ? "Starting…"
                : runStatus === "running"
                  ? "Running…"
                  : runStatus === "done"
                    ? "Opening trace…"
                    : "Run with trace"}
            </button>
            <button
              type="button"
              disabled={!files || files.length === 0}
              onClick={() => setSendAsPrOpen(true)}
              className="rounded-md bg-indigo-500 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
            >
              Send as GitHub PR
            </button>
          </div>
        </div>
        {runError && (
          <p className="mt-2 text-right text-xs text-rose-300">
            Run failed to start: {runError}
          </p>
        )}
      </header>

      <CrawlTabs crawlId={id} active="tests" />

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3">
          <FileTree
            files={files}
            selected={selected}
            onSelect={(name) => {
              setSelected(name);
              setCopied(false);
            }}
          />
        </aside>

        <section className="col-span-12 lg:col-span-8 xl:col-span-9">
          <div className="rounded-xl border border-slate-800 bg-slate-900/70">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <div className="font-mono text-sm text-slate-300 truncate">
                {selected ?? "(no file selected)"}
              </div>
              <button
                disabled={!contents}
                onClick={() => {
                  if (!contents) return;
                  void navigator.clipboard.writeText(contents).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
                className="text-xs px-3 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                {copied ? "copied ✓" : "copy"}
              </button>
            </div>
            <CodeView contents={contents} />
          </div>
        </section>
      </div>

      {sendAsPrOpen && files && id && (
        <SendAsPrDialog
          crawlId={id}
          files={files}
          onClose={() => setSendAsPrOpen(false)}
        />
      )}
    </div>
  );
}

function pickDefaultFile(files: FileInfo[]): FileInfo | null {
  const preferExact = [
    "src/test/java/com/example/SaucedemoSmokeTest.java",
    "tests/test_saucedemo_smoke.py",
  ];
  for (const name of preferExact) {
    const hit = files.find((f) => f.name === name);
    if (hit) return hit;
  }
  // Any *Test.java first, then any test_*.py, else first file.
  const javaTest = files.find((f) => /Test\.java$/.test(f.name));
  if (javaTest) return javaTest;
  const pyTest = files.find((f) => /test_.*\.py$/.test(f.name));
  if (pyTest) return pyTest;
  return files[0] ?? null;
}

function FileTree({
  files,
  selected,
  onSelect,
}: {
  files: FileInfo[] | null;
  selected: string | null;
  onSelect: (name: string) => void;
}): JSX.Element {
  if (files === null) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-500">
        Loading files…
      </div>
    );
  }
  if (files.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">
        No emitted files in this folder.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50">
      <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400">
        Files ({files.length})
      </div>
      <ul className="divide-y divide-slate-800 max-h-[75vh] overflow-auto">
        {files.map((f) => {
          const active = f.name === selected;
          return (
            <li key={f.name}>
              <button
                onClick={() => onSelect(f.name)}
                className={`w-full text-left px-4 py-2 hover:bg-slate-800/60 font-mono text-[11px] flex items-center justify-between gap-2 ${
                  active ? "bg-slate-800/70 text-white" : "text-slate-300"
                }`}
              >
                <span className="truncate">{f.name}</span>
                <LangBadge lang={f.language} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LangBadge({ lang }: { lang: string }): JSX.Element {
  const color =
    lang === "java"
      ? "bg-orange-500/15 text-orange-300 border-orange-500/30"
      : lang === "python"
      ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
      : lang === "xml"
      ? "bg-slate-600/20 text-slate-400 border-slate-600/30"
      : "bg-slate-700/20 text-slate-400 border-slate-600/30";
  return (
    <span className={`text-[9px] uppercase tracking-wide border rounded px-1 ${color}`}>
      {lang}
    </span>
  );
}

function CodeView({ contents }: { contents: string | null }): JSX.Element {
  if (contents === null) {
    return (
      <div className="p-6 text-sm text-slate-500 font-mono">
        loading file…
      </div>
    );
  }
  const lines = contents.split("\n");
  return (
    <div className="overflow-auto max-h-[78vh] text-[12px]">
      <pre className="font-mono leading-5">
        <table className="border-separate border-spacing-0">
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td className="sticky left-0 bg-slate-900/90 select-none text-slate-600 pr-3 pl-4 text-right w-12 align-top">
                  {i + 1}
                </td>
                <td className="pr-4 whitespace-pre text-slate-200">{line || " "}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </pre>
    </div>
  );
}

function ErrorPane({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
      <div className="font-medium">Failed to load files</div>
      <div className="font-mono text-xs mt-1 text-rose-300/80">{message}</div>
    </div>
  );
}
